import { DepartmentLabel, useDepartmentText } from 'model/DepartmentTerminology';
import { Box, Group, Menu, Stack, Tooltip, useMantineTheme } from '@mantine/core';
import Avatar from 'components/Avatar';
import P1Medium from 'components/typography/P1Medium';
import P2Regular from 'components/typography/P2Regular';
import P3Regular from 'components/typography/P3Regular';
import { usePermissionDataProvider } from 'model/PermissionDataProvider';
import { UserFormData } from 'components/forms/user/UserForm';
import { IconDotsVertical, IconPencil, IconTrashX } from '@tabler/icons-react';
import { usePickOrganizations } from 'components/modals/pick-organizations-modal/PickOrganizationsProvider';
import { usePickDepartments } from 'components/modals/pick-departments-modal/PickDepartmentsProvider';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { useCallback, useMemo } from 'react';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import DepartmentBreadcrumb, {
  DepartmentBreadcrumbItem,
  formatDepartmentPath,
} from 'components/department/DepartmentBreadcrumb';
import DepartmentParentReference from 'components/department/DepartmentParentReference';

type EntityType = 'organization' | 'department';

interface PermissionsForEntityProps {
  entityId: number;
  entityName: string;
  entityType: EntityType;
  permissions: number[];
  templateSlug: string;
  departmentBreadcrumbs?: DepartmentBreadcrumbItem[];
  compact?: boolean;
  hideDelete?: boolean;
  summarizeInheritedPermissions?: boolean;
  permissionSources?: {
    key: string;
    label: string;
    permissions: number[];
    templateSlug: string;
  }[];
  value: UserFormData['inOrganizations'] | UserFormData['inDepartments'];
  updatePermissionsForEntity: ({ permissions, templateName }: { permissions: number[]; templateName: string }) => void;
  onEntityDelete(entityId: number): void;
}

/**
 * Renders the entity's, user's permissions for the entity.
 */
export default function PermissionsForEntity({
  entityId,
  entityName,
  entityType,
  permissions,
  templateSlug,
  departmentBreadcrumbs = [],
  compact = false,
  hideDelete = false,
  summarizeInheritedPermissions = false,
  permissionSources,
  value: formValue,
  updatePermissionsForEntity,
  onEntityDelete,
}: PermissionsForEntityProps) {
  const departmentText = useDepartmentText();
  const theme = useMantineTheme();
  const { getPermission, getPermissionTemplate } = usePermissionDataProvider();
  const { confirm } = useConfirm();
  const { pickOrganizations } = usePickOrganizations();
  const { pickDepartments } = usePickDepartments();
  const { isMobile } = useResponsiveDesign();
  const inheritedPermissions = useMemo(
    () =>
      Array.from(
        new Set(permissionSources?.filter(({ key }) => key !== 'direct').flatMap((source) => source.permissions) ?? [])
      ),
    [permissionSources]
  );
  const inheritedPermissionDetails = useMemo(
    () => inheritedPermissions.flatMap((permissionId) => getPermission(permissionId) ?? []),
    [getPermission, inheritedPermissions]
  );
  const inheritanceDetails = useMemo(
    () =>
      permissionSources?.flatMap((source) => {
        if (source.key === 'direct') return [];

        const permissionNames = Array.from(
          new Set(
            source.permissions.flatMap((permissionId) => {
              const permission = getPermission(permissionId);
              return permission ? [permission.permissionName] : [];
            })
          )
        );

        return permissionNames.length > 0 ? [{ key: source.key, label: source.label, permissionNames }] : [];
      }) ?? [],
    [getPermission, permissionSources]
  );
  const permissionSections = useMemo(() => {
    if (!permissionSources) return undefined;

    const displayedPermissionIds = new Set<number>();

    return permissionSources.flatMap((source) => {
      const displayedPermissions = source.permissions.flatMap((permissionId) => {
        const permission = getPermission(permissionId);

        if (!permission || displayedPermissionIds.has(permissionId)) return [];

        displayedPermissionIds.add(permissionId);
        return [permission];
      });

      if (displayedPermissions.length === 0) return [];

      const templateName = getPermissionTemplate(source.templateSlug)?.permissionTemplateName;
      return [
        {
          ...source,
          displayedPermissions,
          accessibleLabel: `${source.label}${templateName ? ` (${templateName})` : ''}`,
        },
      ];
    });
  }, [getPermission, getPermissionTemplate, permissionSources]);
  const templateName = getPermissionTemplate(templateSlug)?.permissionTemplateName;

  /**
   * Confirms the removal of organization/department from the user.
   */
  const confirmEntityDelete = useCallback(() => {
    confirm({
      title:
        entityType === 'organization' ? (
          'Odstránenie organizácie'
        ) : (
          <DepartmentLabel objectLabel="Odstránenie položky úrovne" departmentLabel="Odstránenie strediska" />
        ),
      content:
        entityType === 'organization' ? (
          `Naozaj chcete odstrániť organizáciu ${entityName}?`
        ) : (
          <DepartmentLabel
            objectLabel={`Naozaj chcete odstrániť položku úrovne ${entityName}?`}
            departmentLabel={`Naozaj chcete odstrániť stredisko ${entityName}?`}
          />
        ),
      onConfirm: () => onEntityDelete(entityId),
    });
  }, [entityType, entityName, entityId, confirm, onEntityDelete]);

  /**
   * Updates the permissions of the user for the organization/department.
   */
  const updatePermissions = () => {
    if (entityType === 'organization') {
      pickOrganizations({
        title: '',
        permissionsTitle: `Oprávnenia v ${entityName}`,
        hiddenOrganizations: [],
        initialPermissions: permissions,
        initialTemplateName: formValue[entityId]?.templateName ?? templateSlug,
        pickPermissions: true,
        pickOrganizations: false,
        targetOrganizationIds: [entityId],
        onPick: ({ permissions, templateName }) => {
          updatePermissionsForEntity({ permissions, templateName });
        },
      });
    } else {
      pickDepartments({
        title: '',
        permissionsTitle: `Oprávnenia v ${entityName}`,
        hiddenDepartments: [],
        initialPermissions: permissions,
        inheritedPermissionsByDepartment: { [entityId]: inheritedPermissions },
        initialTemplateName: formValue[entityId]?.templateName ?? templateSlug,
        pickPermissions: true,
        pickDepartments: false,
        targetDepartmentIds: [entityId],
        onPick: ({ permissions, templateName }) => {
          updatePermissionsForEntity({ permissions, templateName });
        },
      });
    }
  };

  if (compact) {
    return (
      <Box
        aria-label={`${departmentText('Zdedené oprávnenia v položke úrovne', 'Zdedené oprávnenia v stredisku')} ${
          formatDepartmentPath(departmentBreadcrumbs) || entityName
        }`}
        bg="gray.0"
        px={10}
        py={6}
        sx={{ border: `1px solid ${theme.colors.gray[2]}`, borderRadius: 4, width: 'fit-content' }}
      >
        <Group spacing={6} noWrap>
          <Avatar fullName={entityName} size="m" bgColor={theme.colors.gray[7]} />
          <DepartmentParentReference breadcrumbs={departmentBreadcrumbs} />
          <DepartmentBreadcrumb
            breadcrumbs={departmentBreadcrumbs}
            compact
            tooltipDetails={<InheritanceTooltipDetails sources={inheritanceDetails} />}
            withPathInTooltip={false}
          />
        </Group>
      </Box>
    );
  }

  return (
    <Box
      aria-label={
        entityType === 'department'
          ? `${departmentText('Oprávnenia v položke úrovne', 'Oprávnenia v stredisku')} ${
              formatDepartmentPath(departmentBreadcrumbs) || entityName
            }`
          : `Oprávnenia v organizácii ${entityName}`
      }
    >
      <Group
        style={{
          border: `1px solid ${theme.colors.gray[2]}`,
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          alignContent: 'center',
        }}
        bg="gray.0"
        py={12}
        px={16}
        position="apart"
        mih={56}
        spacing={16}
      >
        <Group spacing={12}>
          <Avatar fullName={entityName} size="m" bgColor={theme.colors.gray[7]} />
          <Group spacing={8} maw={380}>
            {entityType === 'department' ? (
              <Group spacing={6} noWrap>
                <DepartmentParentReference breadcrumbs={departmentBreadcrumbs} />
                <DepartmentBreadcrumb breadcrumbs={departmentBreadcrumbs.slice(-1)} />
              </Group>
            ) : (
              <P1Medium color="gray.8">{entityName}</P1Medium>
            )}
            {templateName && <P2Regular>({templateName})</P2Regular>}
          </Group>
        </Group>
        <Group spacing={12}>
          <Button
            variant="subtle-gray"
            size="sm"
            leftIcon={<IconPencil stroke="1.5" color={theme.colors.gray[7]} />}
            onClick={updatePermissions}
          >
            Upraviť oprávnenia
          </Button>
          {!hideDelete && (
            <>
              <Box style={{ borderLeft: `1px solid ${theme.colors.gray[2]}` }} h={24}></Box>
              <Menu shadow="md" width={300} position="bottom-end">
                <Menu.Target>
                  <ActionIcon variant="subtle-gray" size={isMobile ? 'sm' : 'md'} aria-label="Akcie oprávnení">
                    <IconDotsVertical stroke="1.5" />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item color="red.8" icon={<IconTrashX stroke={1.5} size={20} />} onClick={confirmEntityDelete}>
                    Zmazať oprávnenia v {entityName}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </>
          )}
        </Group>
      </Group>
      <Group
        style={{
          borderBottom: `1px solid ${theme.colors.gray[2]}`,
          borderLeft: `1px solid ${theme.colors.gray[2]}`,
          borderRight: `1px solid ${theme.colors.gray[2]}`,
          borderBottomLeftRadius: '4px',
          borderBottomRightRadius: '4px',
        }}
        p={16}
        spacing={8}
      >
        {summarizeInheritedPermissions ? (
          <Group spacing={6}>
            {permissions.flatMap((permissionId) => {
              const permission = getPermission(permissionId);
              return permission ? (
                <PermissionPill key={permission.permissionId} permissionName={permission.permissionName} />
              ) : (
                []
              );
            })}
            {inheritedPermissionDetails.length > 0 && (
              <InheritedPermissionsBadge
                permissionNames={inheritedPermissionDetails.map(({ permissionName }) => permissionName)}
              />
            )}
          </Group>
        ) : permissionSections ? (
          permissionSections.map(({ accessibleLabel, displayedPermissions, key }) => (
            <Stack key={key} spacing={4} role="group" aria-label={accessibleLabel}>
              <P1Medium color="gray.9">{accessibleLabel}</P1Medium>
              <Group spacing={6}>
                {displayedPermissions.map((permission) => (
                  <PermissionPill key={permission.permissionId} permissionName={permission.permissionName} />
                ))}
              </Group>
            </Stack>
          ))
        ) : (
          permissions.map(
            (permission) =>
              getPermission(permission) && (
                <PermissionPill key={permission} permissionName={getPermission(permission)!.permissionName} />
              )
          )
        )}
      </Group>
    </Box>
  );
}

/** Renders inherited permission names in a keyboard-accessible count badge. */
function InheritedPermissionsBadge({ permissionNames }: { permissionNames: string[] }) {
  const theme = useMantineTheme();

  return (
    <Tooltip
      label={<TooltipRows rows={permissionNames} />}
      events={{ hover: true, focus: true, touch: true }}
      multiline
      withinPortal
    >
      <Box
        aria-label={`Zdedené oprávnenia: ${permissionNames.join(', ')}`}
        bg="gray.0"
        data-testid="entity-inherited-permissions-badge"
        px={5}
        tabIndex={0}
        sx={{ border: `1px solid ${theme.colors.gray[2]}`, borderRadius: 999, width: 'fit-content' }}
      >
        <P3Regular color="gray.8">(+{permissionNames.length})</P3Regular>
      </Box>
    </Tooltip>
  );
}

/** Renders tooltip content with one explanation per row. */
function TooltipRows({ rows }: { rows: string[] }) {
  return (
    <Stack spacing={2}>
      <P3Regular weight={600}>Zdedené oprávnenia</P3Regular>
      {rows.map((row) => (
        <P3Regular key={row}>• {row}</P3Regular>
      ))}
    </Stack>
  );
}

/** Renders every inherited source path with the permissions contributed by that path. */
function InheritanceTooltipDetails({
  sources,
}: {
  sources: { key: string; label: string; permissionNames: string[] }[];
}) {
  return (
    <Stack spacing={6}>
      {sources.map(({ key, label, permissionNames }) => (
        <Stack key={key} spacing={2}>
          <P3Regular>{label}</P3Regular>
          {permissionNames.map((permissionName) => (
            <P3Regular key={permissionName}>• {permissionName}</P3Regular>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

/** Renders one effective permission label inside a source section. */
function PermissionPill({ permissionName }: { permissionName: string }) {
  const theme = useMantineTheme();

  return (
    <Box
      bg="gray.0"
      data-testid="entity-permission-pill"
      style={{ padding: '0 5px' }}
      sx={{ border: `1px solid ${theme.colors.gray[2]}`, borderRadius: 999, width: 'fit-content' }}
    >
      <P3Regular color="gray.8">{permissionName}</P3Regular>
    </Box>
  );
}
